import { ActionPayloadMap } from '../types';
import { enhancedApi } from '@store/generated/actions';
import { ActionState, ActionType } from '../constants';

/**
 * Feature-specific tag types for actions
 */
const ACTION_TAG_TYPES = ['Action', 'DashboardActions', 'NoteActions'] as const;

// Type for Redux state with API queries
interface RootStateWithQueries {
  api: {
    queries: Record<
      string,
      {
        originalArgs: any;
        data?: { items: any[] };
      }
    >;
  };
}

/**
 * Actions API - extends the generated API with custom tagging and endpoints
 * This ensures we're using a single API instance throughout the app
 * but still maintaining a clean feature-based code organization
 */
export const actionsApi = enhancedApi
  .enhanceEndpoints({
    addTagTypes: ACTION_TAG_TYPES,
    endpoints: {
      // Enhance generated endpoints with tags
      getV2ProviderActionsSuggested: {
        providesTags: result =>
          result
            ? [
                ...result.items.map(({ action_id }) => ({
                  type: 'Action' as const,
                  id: action_id,
                })),
                { type: 'DashboardActions' as const, id: 'LIST' },
              ]
            : [{ type: 'DashboardActions', id: 'LIST' }],
      },
      getV2ProviderNotesByNoteIdActionsSuggested: {
        providesTags: (result, error, { noteId }) =>
          result
            ? [
                ...result.items.map(({ action_id }) => ({
                  type: 'Action' as const,
                  id: action_id,
                })),
                { type: 'NoteActions' as const, id: noteId },
              ]
            : [{ type: 'NoteActions', id: noteId }],
      },
      postV2ProviderActionsByActionIdAccept: {
        invalidatesTags: (result, error, { actionId }) => [
          { type: 'Action', id: actionId },
          { type: 'DashboardActions', id: 'LIST' },
          { type: 'NoteActions', id: 'LIST' },
        ],
        // Add onQueryStarted for optimistic updates
        onQueryStarted: async (
          { actionId, actionAccept },
          { dispatch, queryFulfilled, getState },
        ) => {
          // Create a patch collection to track all optimistic updates
          const patchCollection: { undo: () => void }[] = [];

          try {
            // Find all appropriate cached queries that include this action
            const state = getState() as RootStateWithQueries;
            const queries = state.api?.queries || {};

            const noteQueryKeys = Object.keys(queries).filter(key =>
              key.startsWith('getV2ProviderNotesByNoteIdActionsSuggested'),
            );

            const dashboardQueryKeys = Object.keys(queries).filter(key =>
              key.startsWith('getV2ProviderActionsSuggested'),
            );

            // Apply optimistic updates to note action queries
            for (const queryKey of noteQueryKeys) {
              const patch = dispatch(
                enhancedApi.util.updateQueryData(
                  'getV2ProviderNotesByNoteIdActionsSuggested',
                  queries[queryKey].originalArgs,
                  draft => {
                    // Find the action in the results
                    const actionToUpdate = draft.items.find(a => a.action_id === actionId);
                    if (actionToUpdate) {
                      // Update the action status optimistically
                      actionToUpdate.state = ActionState.ACCEPTED;
                      // Update any other relevant fields based on actionAccept payload
                      if (
                        actionToUpdate.action_type === ActionType.SCHEDULE_APPOINTMENT &&
                        actionAccept?.action_payload
                      ) {
                        Object.assign(actionToUpdate.action_payload, actionAccept.action_payload);
                      }
                    }
                  },
                ),
              );
              patchCollection.push(patch);
            }

            // Apply the same updates to dashboard action queries
            for (const queryKey of dashboardQueryKeys) {
              const patch = dispatch(
                enhancedApi.util.updateQueryData(
                  'getV2ProviderActionsSuggested',
                  queries[queryKey].originalArgs,
                  draft => {
                    const actionToUpdate = draft.items.find(a => a.action_id === actionId);
                    if (actionToUpdate) {
                      actionToUpdate.state = ActionState.ACCEPTED;
                      if (
                        actionToUpdate.action_type === ActionType.SCHEDULE_APPOINTMENT &&
                        actionAccept?.action_payload
                      ) {
                        Object.assign(actionToUpdate.action_payload, actionAccept.action_payload);
                      }
                    }
                  },
                ),
              );
              patchCollection.push(patch);
            }

            // Wait for the actual API request to complete
            await queryFulfilled;
          } catch (error) {
            // If the mutation fails, revert all optimistic updates
            patchCollection.forEach(patch => patch.undo());
          }
        },
      },
      postV2ProviderActionsByActionIdReject: {
        invalidatesTags: (result, error, { actionId }) => [
          { type: 'Action', id: actionId },
          { type: 'DashboardActions', id: 'LIST' },
          { type: 'NoteActions', id: 'LIST' },
        ],
        // Add onQueryStarted for optimistic updates
        onQueryStarted: async ({ actionId }, { dispatch, queryFulfilled, getState }) => {
          // Create a patch collection to track all optimistic updates
          const patchCollection: { undo: () => void }[] = [];

          try {
            // Find all appropriate cached queries that include this action
            const state = getState() as RootStateWithQueries;
            const queries = state.api?.queries || {};

            const noteQueryKeys = Object.keys(queries).filter(key =>
              key.startsWith('getV2ProviderNotesByNoteIdActionsSuggested'),
            );

            const dashboardQueryKeys = Object.keys(queries).filter(key =>
              key.startsWith('getV2ProviderActionsSuggested'),
            );

            // Apply optimistic updates to note action queries
            for (const queryKey of noteQueryKeys) {
              const patch = dispatch(
                enhancedApi.util.updateQueryData(
                  'getV2ProviderNotesByNoteIdActionsSuggested',
                  queries[queryKey].originalArgs,
                  draft => {
                    // Find the action in the results
                    const actionToUpdate = draft.items.find(a => a.action_id === actionId);
                    if (actionToUpdate) {
                      // Update the action status optimistically
                      actionToUpdate.state = ActionState.REJECTED;
                    }
                  },
                ),
              );
              patchCollection.push(patch);
            }

            // Apply the same updates to dashboard action queries
            for (const queryKey of dashboardQueryKeys) {
              const patch = dispatch(
                enhancedApi.util.updateQueryData(
                  'getV2ProviderActionsSuggested',
                  queries[queryKey].originalArgs,
                  draft => {
                    const actionToUpdate = draft.items.find(a => a.action_id === actionId);
                    if (actionToUpdate) {
                      actionToUpdate.state = ActionState.REJECTED;
                    }
                  },
                ),
              );
              patchCollection.push(patch);
            }

            // Wait for the actual API request to complete
            await queryFulfilled;
          } catch (error) {
            // If the mutation fails, revert all optimistic updates
            patchCollection.forEach(patch => patch.undo());
          }
        },
      },
      postV2ProviderActions: {
        invalidatesTags: [
          { type: 'DashboardActions', id: 'LIST' },
          { type: 'NoteActions', id: 'LIST' },
        ],
      },
    },
  })
  .injectEndpoints({
    endpoints: builder => ({
      // Custom endpoints that aren't generated or need specific customization
      // Create a test action (for development)
      createTestAction: builder.mutation<
        any,
        {
          actionType: keyof ActionPayloadMap;
          encounterId: string;
          payload: ActionPayloadMap[keyof ActionPayloadMap];
        }
      >({
        query: ({ actionType, encounterId, payload }) => ({
          url: '/v2/provider/actions',
          method: 'POST',
          body: {
            action_type: actionType,
            encounter_id: encounterId,
            action_payload: payload,
          },
        }),
        invalidatesTags: [
          { type: 'DashboardActions', id: 'LIST' },
          { type: 'NoteActions', id: 'LIST' },
        ],
      }),
    }),
    overrideExisting: false,
  });

// Export the hooks with consistent names to avoid breaking changes
export const {
  useGetV2ProviderActionsSuggestedQuery: useGetSuggestedActionsQuery,
  useGetV2ProviderNotesByNoteIdActionsSuggestedQuery: useGetNoteActionsQuery,
  usePostV2ProviderActionsByActionIdAcceptMutation: useAcceptActionMutation,
  usePostV2ProviderActionsByActionIdRejectMutation: useRejectActionMutation,
  useCreateTestActionMutation,
} = actionsApi;
